import { z } from 'zod';

import {
    CLIENT_CONTEXT_FIELDS,
    isClientContextColumn,
    type ClientContextStoredValue,
    type OnboardingCreateColumn,
    type OnboardingEditColumn,
} from './fields.js';

// ─── Error codes ─────────────────────────────────────────────────────

export const ClientContextErrorCodes = {
    UNKNOWN_FIELD: 'CLIENT_CONTEXT_UNKNOWN_FIELD',
    FIELD_READ_ONLY: 'CLIENT_CONTEXT_FIELD_READ_ONLY',
    VALIDATION_FAILED: 'CLIENT_CONTEXT_VALIDATION_FAILED',
    INDUSTRY_REQUIRED: 'CLIENT_CONTEXT_INDUSTRY_REQUIRED',
    NO_CHANGES: 'CLIENT_CONTEXT_NO_CHANGES',
    CUSTOMER_NOT_CONFIGURED: 'CLIENT_CONTEXT_CUSTOMER_NOT_CONFIGURED',
    MULTIPLE_CUSTOMER_NAMES: 'CLIENT_CONTEXT_MULTIPLE_CUSTOMER_NAMES',
    NOT_FOUND: 'CLIENT_CONTEXT_NOT_FOUND',
    /** The tenant or its customer already has a Nexus onboarding record; edit it instead. */
    ALREADY_EXISTS: 'CLIENT_CONTEXT_ALREADY_EXISTS',
    /** The tenant's customerPartition no longer matches the customer its onboarding record was created for. */
    CUSTOMER_MISMATCH: 'CLIENT_CONTEXT_CUSTOMER_MISMATCH',
    /** Nexus holds no setup snapshot for the tenant, so create must send the New Customer setup. */
    SETUP_REQUIRED: 'CLIENT_CONTEXT_SETUP_REQUIRED',
    /** The customer's setup snapshot belongs to a different tenant; nothing is written. */
    SETUP_OWNED_BY_OTHER_TENANT: 'CLIENT_CONTEXT_SETUP_OWNED_BY_OTHER_TENANT',
    /** Another create for the same customer is running; retry once it finishes. */
    CREATE_IN_PROGRESS: 'CLIENT_CONTEXT_CREATE_IN_PROGRESS',
    /** The client-context API refused the write; `field` / `apiErrorCode` say why. Nothing was saved. */
    API_REJECTED: 'CLIENT_CONTEXT_API_REJECTED',
    /** No client-context API backend is configured for this worker, so nothing can be written. */
    API_NOT_CONFIGURED: 'CLIENT_CONTEXT_API_NOT_CONFIGURED',
    /** The client-context API accepted the write but Nexus could not save its copy. */
    SAVE_NOT_CONFIRMED: 'CLIENT_CONTEXT_SAVE_NOT_CONFIRMED',
    /** The idempotency key already belongs to a different tenant, customer, or payload. */
    IDEMPOTENCY_KEY_REUSED: 'CLIENT_CONTEXT_IDEMPOTENCY_KEY_REUSED',
    /** BigQuery returned a row count or mapping the write cannot safely repair. */
    DATA_INTEGRITY: 'CLIENT_CONTEXT_DATA_INTEGRITY',
    /** The requested competitor slug is already owned by another core partition. */
    COMP_MAPPING_CONFLICT: 'CLIENT_CONTEXT_COMP_MAPPING_CONFLICT',
    /** This deployment has no explicit, routable NEXUS_ENVIRONMENT, so onboarding and Brand Brain writes are refused; nothing was written. */
    WRITES_DISABLED: 'CLIENT_CONTEXT_WRITES_DISABLED',
    /** The edit carries Adge-team-only Brand Brain fields and its sender is not an Adge team member, or could not be verified; nothing was written. */
    NEXUS_FIELDS_FORBIDDEN: 'CLIENT_CONTEXT_NEXUS_FIELDS_FORBIDDEN',
    UNEXPECTED_FAILURE: 'CLIENT_CONTEXT_UNEXPECTED_FAILURE',
} as const;

export type ClientContextErrorCode = (typeof ClientContextErrorCodes)[keyof typeof ClientContextErrorCodes];

/** `field` names the registry column the failure is about, so forms can show it inline. */
export type ClientContextFailure = {
    ok: false;
    code: ClientContextErrorCode;
    message: string;
    field?: string;
    /** The client-context API's own error code, passed through verbatim. */
    apiErrorCode?: string;
};

type Failure = ClientContextFailure;

// ─── Customer name resolution ────────────────────────────────────────

export type ClientContextCustomerNameResult = { ok: true; customerName: string } | Failure;

/**
 * `client_context.customer_name` is one BigQuery customer. A comma-separated
 * value means the tenant spans several customers, so this refuses to guess.
 */
export function resolveClientContextCustomerName(value: string | null | undefined): ClientContextCustomerNameResult {
    if (!value?.trim()) {
        return {
            ok: false,
            code: ClientContextErrorCodes.CUSTOMER_NOT_CONFIGURED,
            message: 'No BigQuery customer is configured for this organization',
        };
    }
    const names = value.split(',').map((name) => name.trim()).filter(Boolean);
    if (names.length !== 1) {
        return {
            ok: false,
            code: ClientContextErrorCodes.MULTIPLE_CUSTOMER_NAMES,
            message: 'This organization resolves to more than one BigQuery customer; exactly one is required',
        };
    }
    return { ok: true, customerName: names[0]! };
}

// ─── Onboarding validation ───────────────────────────────────────────

export type OnboardingCreateValues = Partial<Record<OnboardingCreateColumn, ClientContextStoredValue>> & {
    industry: string;
};

export type OnboardingEditValues = Partial<Record<OnboardingEditColumn, ClientContextStoredValue>>;

export type OnboardingCreateValidation = { ok: true; values: OnboardingCreateValues } | Failure;
export type OnboardingEditValidation = { ok: true; changes: OnboardingEditValues } | Failure;

/** `industry` is required because it is also sent in the clients_params setup row the client-context API writes. */
export function validateOnboardingCreate(fields: Record<string, unknown>): OnboardingCreateValidation {
    const parsed = parseOnboardingFields(fields, 'create');
    if (!parsed.ok) return parsed;

    const industry = parsed.values.industry;
    if (typeof industry !== 'string') {
        return {
            ok: false,
            code: ClientContextErrorCodes.INDUSTRY_REQUIRED,
            message: 'industry is required',
            field: 'industry',
        };
    }
    return { ok: true, values: { ...parsed.values, industry } };
}

export function validateOnboardingEdit(changes: Record<string, unknown>): OnboardingEditValidation {
    if (Object.keys(changes).length === 0) {
        return { ok: false, code: ClientContextErrorCodes.NO_CHANGES, message: 'No onboarding changes to save' };
    }
    const parsed = parseOnboardingFields(changes, 'edit');
    return parsed.ok ? { ok: true, changes: parsed.values as OnboardingEditValues } : parsed;
}

function parseOnboardingFields(
    fields: Record<string, unknown>,
    mode: 'create' | 'edit',
): { ok: true; values: Partial<Record<OnboardingCreateColumn, ClientContextStoredValue>> } | Failure {
    const values: Partial<Record<OnboardingCreateColumn, ClientContextStoredValue>> = {};

    for (const [field, raw] of Object.entries(fields)) {
        if (!isClientContextColumn(field)) {
            return {
                ok: false,
                code: ClientContextErrorCodes.UNKNOWN_FIELD,
                message: `Unknown onboarding field: ${field}`,
                field,
            };
        }
        const definition = CLIENT_CONTEXT_FIELDS[field];
        const isWritable = definition.write === 'editable' || (mode === 'create' && definition.write === 'create-only');
        if (!isWritable) {
            return { ok: false, code: ClientContextErrorCodes.FIELD_READ_ONLY, message: readOnlyMessage(field, mode), field };
        }

        const parsed = definition.schema.safeParse(raw);
        if (!parsed.success) {
            return {
                ok: false,
                code: ClientContextErrorCodes.VALIDATION_FAILED,
                message: `${field}: ${parsed.error.issues[0]?.message ?? 'Invalid value'}`,
                field,
            };
        }
        values[field as OnboardingCreateColumn] = parsed.data;
    }
    return { ok: true, values };
}

function readOnlyMessage(field: string, mode: 'create' | 'edit'): string {
    if (field === 'customer_name') return 'customer_name comes from the organization customerPartition';
    return mode === 'edit' ? `${field} cannot be changed after onboarding is created` : `${field} is read-only`;
}

// ─── API request bodies ──────────────────────────────────────────────

/**
 * Whether this deployment accepts onboarding and Brand Brain writes. The server flag decides;
 * the UI only reads this to hide create / initialize / edit controls.
 */
export const OnboardingWritesAvailabilitySchema = z.object({
    writesEnabled: z.boolean(),
});

export type OnboardingWritesAvailability = z.infer<typeof OnboardingWritesAvailabilitySchema>;

export const TenantOnboardingFetchBodySchema = z.object({
    idempotencyKey: z.string().min(1),
});

/** New Organization retry (CREATE_TENANT): the setup is mandatory, exactly as on provisioning. */
const onboardingIdempotencyKeySchema = z.string().uuid();

export const CompKeySchema = z.string().trim().min(1).max(255);

export function normalizeCompKey(value: string): string {
    return value.trim().toLowerCase();
}

// ─── New partitions ──────────────────────────────────────────────────
// Only a partition Nexus is about to create is checked. Existing customer and
// competitor partitions are matched as they are, so mappings made before this
// rule keep working and are never renamed. ASCII only, so an uppercase letter
// or a Cyrillic lookalike (`а` for `a`) can't create a second, near-identical customer.

const NEW_PARTITION_PATTERN = /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/;

export const NEW_PARTITION_RULE =
    'Use only lowercase letters a-z, digits 0-9, hyphens and underscores, starting and ending with a letter or digit';

export type NewPartitionField = 'customerPartition' | 'compKey';

export function validateNewPartition(value: string, field: NewPartitionField): ClientContextFailure | null {
    if (NEW_PARTITION_PATTERN.test(value.trim())) return null;
    return {
        ok: false,
        code: ClientContextErrorCodes.VALIDATION_FAILED,
        message: NEW_PARTITION_RULE,
        field,
    };
}

/** A comp key that differs from the organization's own customer partition is a new competitor partition. */
export function isNewCompPartition(compKey: string, customerName: string): boolean {
    return normalizeCompKey(compKey) !== normalizeCompKey(customerName);
}

export const TenantOnboardingCreateBodySchema = z.object({
    idempotencyKey: onboardingIdempotencyKeySchema,
    compKey: CompKeySchema,
    fields: z.record(z.unknown()),
    setup: z.record(z.unknown()),
});

/**
 * Existing / legacy organization (IDENTITY_HUB_UPDATE_TENANT on the tenant) that has no
 * Nexus onboarding record yet. `setup` is omitted when Nexus already holds the tenant's setup
 * snapshot; when sent, the client-context API writes it to BigQuery and Nexus saves the snapshot.
 */
export const TenantOnboardingInitializeBodySchema = z.object({
    idempotencyKey: onboardingIdempotencyKeySchema,
    compKey: CompKeySchema,
    fields: z.record(z.unknown()),
    setup: z.record(z.unknown()).optional(),
});

export const TenantOnboardingEditBodySchema = z.object({
    idempotencyKey: z.string().min(1),
    compKey: CompKeySchema.optional(),
    changes: z.record(z.unknown()),
});

// ─── Event payloads ──────────────────────────────────────────────────
// Values are normalized by the API; the worker re-validates before writing.

export const TenantOnboardingFetchRequestedPayload = z.object({
    tenantId: z.string().min(1),
    customerName: z.string().min(1),
});

export type TenantOnboardingFetchRequestedPayload = z.infer<typeof TenantOnboardingFetchRequestedPayload>;

/** New Organization review: the exact customer name to look up. Read-only; nothing is written. */
export const CustomerPartitionCheckRequestedPayload = z
    .object({ customerName: z.string().min(1) })
    .strict();

export type CustomerPartitionCheckRequestedPayload = z.infer<typeof CustomerPartitionCheckRequestedPayload>;

/**
 * What the read-only check found for one exact customer name. `existsInBigQuery`
 * is true when either authoritative onboarding table has a row; the row counts
 * are reported so a duplicate is visible. `existsInNexusOnboarding` is true when
 * Nexus already holds an onboarding record or setup snapshot under that name.
 */
export const CustomerPartitionCheckResultSchema = z.object({
    customerName: z.string(),
    existsInBigQuery: z.boolean(),
    existsInNexusOnboarding: z.boolean(),
    rows: z.object({
        clientContext: z.number().int().nonnegative(),
        clientsParams: z.number().int().nonnegative(),
    }),
});

export type CustomerPartitionCheckResult = z.infer<typeof CustomerPartitionCheckResultSchema>;

export const TenantOnboardingCreateRequestedPayload = z.object({
    tenantId: z.string().min(1),
    customerName: z.string().min(1),
    idempotencyKey: onboardingIdempotencyKeySchema,
    compKey: CompKeySchema,
    fields: z.record(z.unknown()),
    setup: z.record(z.unknown()).optional(),
});

export type TenantOnboardingCreateRequestedPayload = z.infer<typeof TenantOnboardingCreateRequestedPayload>;

export const TenantOnboardingEditRequestedPayload = z.object({
    tenantId: z.string().min(1),
    customerName: z.string().min(1),
    compKey: CompKeySchema.optional(),
    /** client_context columns, plus Brand Brain's Nexus-owned fields; the worker splits them by registry. */
    changes: z.record(z.unknown()),
});

export type TenantOnboardingEditRequestedPayload = z.infer<typeof TenantOnboardingEditRequestedPayload>;

// ─── Results ─────────────────────────────────────────────────────────

export const TenantOnboardingReadModelSchema = z.object({
    customerName: z.string(),
    /** Effective COMP key: the mapped slug, or the core key when no mapping exists. */
    compKey: z.string(),
    /** Nexus's onboarding record (Postgres) — editable onboarding. */
    exists: z.boolean(),
    values: z.record(z.unknown()),
    /**
     * Nexus's setup snapshot (Postgres `tenant_onboarding_setups`) — read-only. `exists: false`
     * means Nexus holds no snapshot; there is no fallback to BigQuery clients_params.
     */
    setup: z.object({
        exists: z.boolean(),
        values: z.record(z.unknown()),
    }),
});

export type TenantOnboardingReadModel = z.infer<typeof TenantOnboardingReadModelSchema>;

/** Returned only after the client-context API accepted the create and Nexus saved its record. */
export const TenantOnboardingCreateResultSchema = z.object({
    customerName: z.string(),
});

export type TenantOnboardingCreateResult = z.infer<typeof TenantOnboardingCreateResultSchema>;

export const TenantOnboardingEditResultSchema = z.object({
    customerName: z.string(),
    updatedFields: z.array(z.string()),
});

export type TenantOnboardingEditResult = z.infer<typeof TenantOnboardingEditResultSchema>;
